import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NumPadKeys } from "./NumPadKeys";
import { grp, useNumPadInput } from "../../hooks/useNumPadInput";
import { blurOtherField } from "./padFocus";
import css from "./NumPadPop.module.css";

/**
 * NumPadPop — React-порт design-references/numpad-pop.js (A42, M.5):
 * компактная якорная клавиатура-поповер у ячейки, пара к большому
 * NumPad-шиту. Семантика ввода = NumPad (общий useNumPadInput: fresh,
 * long-press ⌫ 480мс = очистка, max → shake + hint 1400мс), но протокол
 * закрытия СВОЙ (N:57-63): тап МИМО = commit, Esc = отмена, ✓ = commit;
 * программное open=false хостом = отмена БЕЗ commit (в отличие от шита).
 * «Далее»-цепочек НЕТ (A42). Пусто-дэш «—» только в превью; commit
 * пустого = 0. Скролл вне поповера = «тап мимо» (commit) — портал в body,
 * иначе поповер отклеился бы от якоря (паттерн Dropdown).
 *
 * place() — по Dropdown/эталону N:64-75: left-кламп 6px, вниз от якоря
 * +4px → вверх → кламп. Синглтон эталона (N:76-78): открытие нового попа
 * гасит предыдущий БЕЗ commit (module-scope реестр); закрытие Dropdown/
 * NumPad отдельного протокола не требует — их собственный outside-
 * pointerdown/backdrop ловит тот же тап, который открывает поп.
 * Роль-акцент — из html[data-role] через var(--role) (эталон setRole).
 *
 * UC-расширение (эталон numpad-pop.js v7, аддитивно): decimals>0 —
 * клавиша «.» в нижнем ряду из 4 (грид r4), дробь до N знаков, onCommit
 * получает float; suffix («UC») рисуется ПОСЛЕ числа вместо ₽-префикса
 * (N:47-50). Вызовы без decimals/suffix ведут себя байт-в-байт как раньше.
 *
 * extraKeys-расширение (24.08, владелец, аддитивно): полноширинные
 * клавиши-действия ПОД сеткой цифр (ставка ОО: «записать»/«перезаписать»).
 * Тап = закрытие БЕЗ onCommit + onPress(текущее значение) — commit-протокол
 * (мимо/✓/Esc) не меняется; без пропа поведение байт-в-байт как раньше.
 */
export interface NumPadPopProps {
  open: boolean;
  /** Якорная ячейка (обычно e.currentTarget кнопки-ячейки). */
  anchor: HTMLElement | null;
  label?: string;
  mode?: "money" | "int";
  value?: number | string;
  /** Юнит int-режима («чел», «мин»). */
  unit?: string;
  max?: number;
  /** Дробь до N знаков + клавиша «.»; onCommit получает float (эталон v7). */
  decimals?: number;
  /** Суффикс ПОСЛЕ числа вместо ₽-префикса («UC»). */
  suffix?: string;
  /** Тап мимо / ✓. Зовётся ПОСЛЕ onClose (хост может открыть следующий). */
  onCommit: (v: number) => void;
  /** Клавиши-действия под сеткой (extraKeys-расширение): тап = закрыть без onCommit + onPress(значение). */
  extraKeys?: { label: string; disabled?: boolean; onPress: (v: number) => void }[];
  /** Хост сбрасывает open; при Esc — единственный вызов (без commit). */
  onClose: () => void;
  /** Поверх Dropdown (+20) и TimePicker (+40) — как NumPad-шит. */
  zIndex?: number | string;
}

/* Синглтон (эталон N:76): open() нового сперва close(false) предыдущего. */
let activeCancel: (() => void) | null = null;

/** Грейс скролла после открытия: резидуальный инерционный скролл/PTR-резина
 * в момент тапа по ячейке не должны мгновенно закоммитить нетронутое значение. */
const SCROLL_GRACE_MS = 150;

export function NumPadPop({
  open,
  anchor,
  label,
  mode = "money",
  value,
  unit,
  max,
  decimals,
  suffix,
  onCommit,
  extraKeys,
  onClose,
  zIndex = "calc(var(--z-modal) + 60)",
}: NumPadPopProps) {
  const [entered, setEntered] = useState(false);
  const [shk, setShk] = useState(0);
  const popRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef(true);
  const input = useNumPadInput({ mode, unit, max, decimals, suffix, onBump: () => setShk((n) => n + 1) });

  const close = (commit: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onClose();
    if (commit) onCommit(input.val());
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  const initRef = useRef({ init: input.init, value });
  initRef.current = { init: input.init, value };

  /* Пересброс на открытие/смену якоря — эталонный open() строит заново. */
  useEffect(() => {
    if (!open) return;
    activeCancel?.();
    const mine = () => closeRef.current(false);
    activeCancel = mine;
    doneRef.current = false;
    blurOtherField(); /* 06.09 (владелец): одна клавиатура за раз — фокус (и клавиатура ОС) уходит из чужого поля */
    initRef.current.init(initRef.current.value);
    setShk(0);
    return () => {
      if (activeCancel === mine) activeCancel = null;
    };
  }, [open, anchor]);

  /* place() эталона (N:64-75): координаты вьюпорта (портал в body).
     24.08: финальный top/left клампятся ВСЕГДА (правило дропдаунов 21.08 —
     весь поп в экране, даже если якорь частично за краем), и пере-раскладка
     через ResizeObserver — шрифты доезжают после первого layout'а и расширяют поп. */
  useLayoutEffect(() => {
    if (!open) return;
    const pop = popRef.current;
    if (!pop || !anchor) return;
    const place = () => {
      const ar = anchor.getBoundingClientRect();
      const ph = pop.offsetHeight;
      const pw = pop.offsetWidth;
      const left = Math.max(6, Math.min(ar.left, window.innerWidth - pw - 6));
      const spaceDown = window.innerHeight - ar.bottom - 8;
      const spaceUp = ar.top - 8;
      let top: number;
      if (ph <= spaceDown) top = ar.bottom + 4;
      else if (ph <= spaceUp) top = ar.top - ph - 4;
      else top = ar.bottom + 4;
      top = Math.max(6, Math.min(top, window.innerHeight - ph - 6));
      pop.style.left = `${left}px`;
      pop.style.top = `${top}px`;
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(pop);
    return () => ro.disconnect();
  }, [open, anchor]);

  /* Появление 130мс — класс .on через rAF (эталон N:99). */
  useEffect(() => {
    if (!open) return setEntered(false);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  /* Тап мимо = commit (setTimeout(0) пропускает открывший тап — N:100),
     Esc capture = отмена (N:62), скролл вне = «тап мимо» — слушатель тоже
     отложен setTimeout(0) + грейс SCROLL_GRACE_MS (в эталоне N:99-100
     scroll-слушателя нет вовсе — коммит от хвоста инерции чужд протоколу). */
  useEffect(() => {
    if (!open) return;
    const t0 = performance.now();
    const inPop = (e: Event) => e.target instanceof Node && popRef.current?.contains(e.target);
    const onDown = (e: globalThis.PointerEvent) => {
      if (!inPop(e)) closeRef.current(true);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeRef.current(false);
    };
    const onScroll = (e: Event) => {
      if (performance.now() - t0 < SCROLL_GRACE_MS) return;
      if (!inPop(e)) closeRef.current(true);
    };
    const t = window.setTimeout(() => {
      document.addEventListener("pointerdown", onDown, true);
      document.addEventListener("scroll", onScroll, true);
    }, 0);
    document.addEventListener("keydown", onKey, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  if (!open || !anchor) return null;
  /* Группировка тысяч только целой части: «1 234.56» (эталон v7 N:47). */
  const di = input.digits.indexOf(".");
  const disp = di < 0 ? grp(input.digits) : grp(input.digits.slice(0, di)) + input.digits.slice(di);
  return createPortal(
    <div className={css.host} style={{ zIndex }}>
      <div ref={popRef} className={`${css.pop} ${entered ? css.on : ""}`} role="dialog" aria-label={label ?? "Число"}>
        <div className={css.head}>
          <span className={css.label}>{label ?? ""}</span>
          <span key={shk} className={`${css.preview} ${shk ? css.shk : ""}`}>
            {input.digits === "" ? (
              <span className={`${css.val} ${css.dash}`}>—</span>
            ) : suffix ? (
              <>
                <span className={css.val}>{disp}</span>
                <span className={css.unit}>{suffix}</span>
              </>
            ) : mode === "money" ? (
              <span className={css.val}>₽{disp}</span>
            ) : (
              <>
                <span className={css.val}>{input.digits}</span>
                {unit ? <span className={css.unit}>{unit}</span> : null}
              </>
            )}
          </span>
        </div>
        <div className={`${css.hint} ${input.hint.on ? css.on : ""}`}>{input.hint.text}</div>
        <NumPadKeys
          styles={{ keys: css.keys!, row: css.row!, key: css.key!, del: css.del!, okk: css.okk!, fl: css.fl!, r4: css.r4! }}
          dot={!!decimals && decimals > 0}
          onDigit={input.append}
          onDigitLong={(d) => { input.append(d); close(true); }}
          onOk={() => close(true)}
          onDelDown={input.delDown}
          onDelUp={input.delUp}
          onDelClick={input.delClick}
        />
        {extraKeys?.length ? (
          <div className={css.xkeys}>
            {extraKeys.map((k) => (
              <button
                key={k.label} type="button" className={css.xkey} disabled={k.disabled}
                onClick={() => { const v = input.val(); if (doneRef.current) return; doneRef.current = true; onClose(); k.onPress(v); }}
              >{k.label}</button>
            ))}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
